import React, { useState, useMemo, useEffect } from 'react';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import Expander from '@amzn/meridian/expander';
import Select, { SelectOption } from '@amzn/meridian/select';
import { TEXTS } from '../../utils/constants';
import { formatDuration } from './laborOpportunitiesModel';
import './shared/flowPlannerShared.css';

const METRICS = [
  { value: 'all', label: 'All Metrics' },
  { value: 'volume', label: 'Volume' },
  { value: 'rate', label: 'Rate' },
  { value: 'headcount', label: 'Headcount' }
];

const DEPTH_INDENT_PX = { 0: 0, 1: 24, 2: 48 };
const formatNumber = (value) => (value === null ? 'N/A' : (value || 0).toLocaleString());

const METRIC_FORMATTERS = {
  volume: (s) => formatNumber(s.volume),
  rate: (s) => formatNumber(s.rate),
  workTime: (s) => `${s.workTime || 0}m`,
  headcount: (s) => (s.headcount || 0).toFixed(2)
};

const ToggleButton = ({ open, onClick, label }) => (
  <button type="button" className="fp-plan-toggle" onClick={onClick} aria-expanded={open} aria-label={label}>
    {open ? '▼' : '▶'}
  </button>
);

const ToggleSpacer = () => <span className="fp-plan-toggle-spacer" aria-hidden="true" />;

const PVT_BREACH_THRESHOLD = 0.95;

const PvtPill = ({ pvt }) => {
  if (!pvt) return null;
  const planned = pvt.planned || 0;
  const target = pvt.target || 0;
  if (!planned && !target) return null;
  // Only flag a breach when achievement falls below 95% — small overshoots
  // (e.g. 285/286) shouldn't read as alarm-level.
  const breach = target > 0 && planned / target < PVT_BREACH_THRESHOLD;
  return (
    <span className="fp-plan-pvt" data-breach={breach} title={`Planned ${planned} / Target ${target}`}>
      <span>{planned.toLocaleString()}</span>
      <span className="fp-plan-pvt-sep">/</span>
      <span>{target.toLocaleString()}</span>
    </span>
  );
};

const ThreeCellGrid = ({ slot }) => {
  const allZero = !slot.volume && !slot.rate && !slot.headcount;
  if (allZero) return <div className="fp-plan-mini-empty">—</div>;
  return (
    <div className="fp-plan-mini-stack">
      <span className="fp-plan-mini-primary">
        <span className="fp-plan-mini-label">Vol</span>
        {formatNumber(slot.volume)}
      </span>
      <span className="fp-plan-mini-secondary">
        <span className="fp-plan-mini-label">Rate</span>
        {formatNumber(slot.rate)}
        <span className="sep" aria-hidden="true">
          ·
        </span>
        <span className="fp-plan-mini-label">HC</span>
        <span className="fp-plan-mini-hc">{(slot.headcount || 0).toFixed(1)}</span>
      </span>
    </div>
  );
};

const SingleMetricCell = ({ slot, metric }) => {
  const formatted = METRIC_FORMATTERS[metric](slot);
  const empty = formatted === '0' || formatted === '0.00' || formatted === '0m';
  return <span className={`fp-plan-value${empty ? ' fp-plan-value-muted' : ''}`}>{empty ? '—' : formatted}</span>;
};

const GroupRow = ({ row, depth, isOpen, onToggle }) => {
  const isTopLevel = depth === 0;
  return (
    <tr>
      <td className="fp-plan-cell fp-plan-row-label">
        <span className="fp-plan-row-label-inner" style={{ '--depth-indent': `${DEPTH_INDENT_PX[depth] ?? 0}px` }}>
          <ToggleButton
            open={isOpen}
            onClick={onToggle}
            label={`${isOpen ? 'Collapse' : 'Expand'} ${row.displayName}`}
          />
          <span className="fp-plan-row-text">{row.displayName}</span>
        </span>
      </td>
      <td className="fp-plan-cell fp-plan-cell--center">
        <span className="fp-plan-value-muted">—</span>
      </td>
      {row.slots.map((slot, i) => {
        const empty = !slot.volume && !slot.headcount;
        return (
          <td key={i} className="fp-plan-cell fp-plan-cell--center">
            {empty ? (
              <span className="fp-plan-value-muted">—</span>
            ) : (
              <span className={`fp-plan-value${isTopLevel ? ' is-emphasized' : ''}`}>
                {(slot.volume || 0).toLocaleString()}
                {!isOpen && isTopLevel && (
                  <span className="fp-plan-aggregate-secondary">{`(${(slot.headcount || 0).toFixed(1)} HC)`}</span>
                )}
              </span>
            )}
          </td>
        );
      })}
    </tr>
  );
};

const DetailRow = ({ row, depth, metric }) => (
  <tr>
    <td className="fp-plan-cell fp-plan-row-label">
      <span className="fp-plan-row-label-inner" style={{ '--depth-indent': `${DEPTH_INDENT_PX[depth] ?? 0}px` }}>
        <ToggleSpacer />
        <span className="fp-plan-row-text">
          {row.displayName}
          {row.splitPercentage != null && (
            <span className="fp-plan-split-pct">{` (${row.splitPercentage.toFixed(0)}%)`}</span>
          )}
        </span>
      </span>
    </td>
    <td className="fp-plan-cell fp-plan-cell--center">
      {row.pvt ? <PvtPill pvt={row.pvt} /> : <span className="fp-plan-value-muted">—</span>}
    </td>
    {row.slots.map((slot, i) => (
      <td key={i} className="fp-plan-cell fp-plan-cell--center">
        {metric === 'all' ? <ThreeCellGrid slot={slot} /> : <SingleMetricCell slot={slot} metric={metric} />}
      </td>
    ))}
  </tr>
);

const WipRow = ({ stowWIP, columnCount }) => (
  <tr className="fp-plan-wip-row">
    <td className="fp-plan-cell fp-plan-row-label">
      <span className="fp-plan-row-label-inner">
        <ToggleSpacer />
        <span className="fp-plan-wip-chip">WIP</span>
        <span className="fp-plan-row-text">Stow WIP</span>
      </span>
    </td>
    <td className="fp-plan-cell fp-plan-cell--center">
      <span className="fp-plan-value-muted">—</span>
    </td>
    {stowWIP.slice(0, columnCount).map((value, i) => (
      <td
        key={i}
        className="fp-plan-cell fp-plan-cell--center"
        title="Work In Progress: Cumulative Induct - Cumulative Stow">
        {value === 0 ? (
          <span className="fp-plan-value-muted">—</span>
        ) : (
          <span className="fp-plan-value is-emphasized">{value.toLocaleString()}</span>
        )}
      </td>
    ))}
  </tr>
);

function renderNode(node, depth, expanded, toggle, metric) {
  const isGroup = !!node.children;
  const key = node.key || node.displayName;

  if (!isGroup) {
    return [<DetailRow key={key} row={node} depth={depth} metric={metric} />];
  }

  const isOpen = expanded.includes(key);
  const elements = [<GroupRow key={key} row={node} depth={depth} isOpen={isOpen} onToggle={() => toggle(key)} />];

  if (isOpen) {
    node.children.forEach((child) => {
      elements.push(...renderNode(child, depth + 1, expanded, toggle, metric));
    });
  }
  return elements;
}

function collectAllGroupKeys(nodes) {
  const keys = [];
  for (const node of nodes) {
    if (node.children && !node.hidden) {
      keys.push(node.key || node.displayName);
      keys.push(...collectAllGroupKeys(node.children));
    }
  }
  return keys;
}

const LaborPlanGrid = ({ gridCycleData, intervals = [] }) => {
  const [expanded, setExpanded] = useState([]);
  const [selectedMetric, setSelectedMetric] = useState('all');
  const [isExpanded, setIsExpanded] = useState(true);

  const toggle = (key) => setExpanded((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));

  const { rows = [], stowWIP = [] } = gridCycleData || {};
  const allGroupKeys = useMemo(() => collectAllGroupKeys(rows), [rows]);

  useEffect(() => setExpanded([]), [gridCycleData]);
  if (!gridCycleData) return null;

  const wipBeforeKey = 'STOW';
  const allExpanded = allGroupKeys.length > 0 && allGroupKeys.every((k) => expanded.includes(k));
  const expandAll = () => setExpanded(allGroupKeys);
  const collapseAll = () => setExpanded([]);

  const slotCount = intervals.length || rows[0]?.slots.length || 0;

  return (
    <div className="fp-theme">
      <Column spacing="300" alignmentHorizontal="stretch" width="100%">
        <Expander open={isExpanded} onChange={setIsExpanded} title="Labour Plan" type="inline" spacingInset="300">
          <Column spacing="100" alignmentHorizontal="stretch" width="100%">
            <Row alignmentHorizontal="end" alignmentVertical="center" spacing="300" width="100%">
              <Text type={TEXTS.T3}>View:</Text>
              <Select value={selectedMetric} onChange={setSelectedMetric} width={140} size="small">
                {METRICS.map((m) => (
                  <SelectOption key={m.value} value={m.value} label={m.label} />
                ))}
              </Select>
            </Row>
            <table className="fp-plan-table">
              <thead>
                <tr>
                  <th
                    className="fp-plan-header-cell fp-plan-header-cell--process"
                    scope="col"
                    aria-label="Process column">
                    <span className="fp-plan-row-label-inner">
                      <ToggleButton
                        open={allExpanded}
                        onClick={allExpanded ? collapseAll : expandAll}
                        label={allExpanded ? 'Collapse all rows' : 'Expand all rows'}
                      />
                      <span>Process</span>
                    </span>
                  </th>
                  <th className="fp-plan-header-cell fp-plan-header-cell--pvt" scope="col">
                    PVT
                  </th>
                  {intervals.map((iv, idx) => (
                    <th
                      key={idx}
                      className={`fp-plan-header-cell fp-plan-header-cell--slot${
                        idx === slotCount - 1 ? ' is-last' : ''
                      }`}
                      scope="col">
                      <div className="fp-summary-slot-line">
                        <span className="fp-summary-slot-primary">{iv.name || `P${idx + 1}`}</span>
                        {iv.startLabel && iv.endLabel && (
                          <span className="fp-summary-slot-secondary">{`${iv.startLabel}–${iv.endLabel}`}</span>
                        )}
                        {iv.endTime && iv.startTime && (
                          <span className="fp-summary-slot-tertiary">{formatDuration(iv.endTime - iv.startTime)}</span>
                        )}
                      </div>
                      <div className="fp-summary-slot-indicator" data-status="info" />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.flatMap((row) => {
                  if (row.hidden) return [];
                  const rendered = renderNode(row, 0, expanded, toggle, selectedMetric);
                  if (row.key === wipBeforeKey && stowWIP.length > 0) {
                    return [<WipRow key="__wip__" stowWIP={stowWIP} columnCount={slotCount} />, ...rendered];
                  }
                  return rendered;
                })}
              </tbody>
            </table>
          </Column>
        </Expander>
      </Column>
    </div>
  );
};

export default LaborPlanGrid;
